import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Button, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE_URL } from "config.js";

function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "General Inquiry",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Pre-fill user name and email from localStorage on mount
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const user = JSON.parse(storedUser);
        const derivedName = user.name || (user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : "");
        setFormData((prev) => ({
          ...prev,
          name: derivedName || prev.name,
          email: user.email || prev.email,
        }));
      }
    } catch (e) {
      console.error("Error reading stored user session:", e);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMsg("");

    // Front-end validation
    if (!formData.name.trim()) {
      return setErrorMessage("Please enter your full name.");
    }
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
      return setErrorMessage("Please enter a valid email address.");
    }
    if (!formData.message.trim()) {
      return setErrorMessage("Please enter your message before submitting.");
    }
    if (formData.message.trim().length < 10) {
      return setErrorMessage("Message should be at least 10 characters long.");
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.post(
        `${API_BASE_URL}/support/contact`,
        {
          name: formData.name.trim(),
          email: formData.email.trim(),
          subject: formData.subject,
          message: formData.message.trim(),
        },
        { headers }
      );

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Message Sent!",
          text: response.data.message || "Thank you for reaching out! Our support team will get back to you shortly.",
          confirmButtonColor: "#014c95",
        });

        // Reset message content while preserving user details
        setFormData((prev) => ({
          ...prev,
          subject: "General Inquiry",
          message: "",
        }));
        setSuccessMsg("Your support request has been submitted.");
      } else {
        setErrorMessage(response.data.message || "Failed to submit request.");
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      const serverMsg = error.response?.data?.message || "An unexpected error occurred. Please try again later.";
      setErrorMessage(serverMsg);
      Swal.fire({
        icon: "error",
        title: "Submission Error",
        text: serverMsg,
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container fluid className="p-4" style={{ marginTop: "20px" }}>
      {/* Header Banner */}
      <Row className="mb-4">
        <Col lg={12} className="text-center">
          <div className="d-inline-flex align-items-center justify-content-center bg-primary text-white rounded-circle mb-3" style={{ width: "60px", height: "60px", backgroundColor: "#014c95" }}>
            <i className="fas fa-headset fa-2x"></i>
          </div>
          <h2 className="fw-bold mb-1 mt-0" style={{ fontWeight: "700", color: "#1e293b" }}>
            Contact Support
          </h2>
          <p className="text-muted mx-auto" style={{ fontWeight: "600", fontSize: "1.05rem", maxWidth: "650px" }}>
            Have a question, need assistance with your campaigns, or want to share feedback? Fill out the form below and our team will get back to you.
          </p>
        </Col>
      </Row>

      <Row className="justify-content-center">
        {/* Main Contact Form */}
        <Col lg={7} md={8} className="mb-4">
          <Card className="shadow-sm border-0" style={{ borderRadius: "16px", border: "1px solid #e2e8f0" }}>
            <Card.Body className="p-4 p-md-5">
              <h4 className="fw-bold mb-4" style={{ fontWeight: "700", color: "#014c95" }}>
                <i className="fas fa-paper-plane me-2"></i> Send Us a Message
              </h4>

              {errorMessage && (
                <Alert variant="danger" className="border-0 shadow-sm mb-4" style={{ borderRadius: "10px" }}>
                  <i className="fas fa-exclamation-triangle me-2"></i> {errorMessage}
                </Alert>
              )}

              {successMsg && (
                <Alert variant="success" className="border-0 shadow-sm mb-4" style={{ borderRadius: "10px" }}>
                  <i className="fas fa-check-circle me-2"></i> {successMsg}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="fw-bold text-dark" style={{ fontWeight: "600" }}>
                        Full Name <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        required
                        style={{ borderRadius: "8px", border: "1.5px solid #cbd5e1", padding: "10px 14px" }}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="fw-bold text-dark" style={{ fontWeight: "600" }}>
                        Email Address <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="name@example.com"
                        required
                        style={{ borderRadius: "8px", border: "1.5px solid #cbd5e1", padding: "10px 14px" }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold text-dark" style={{ fontWeight: "600" }}>
                    Category / Subject
                  </Form.Label>
                  <Form.Control
                    as="select"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    style={{ borderRadius: "8px", border: "1.5px solid #cbd5e1", padding: "10px 14px" }}
                  >
                    <option value="New Account Request / Demo">New Account Request / Demo</option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Technical & Platform Support">Technical & Platform Support</option>
                    <option value="Billing & Credits Question">Billing & Credits Question</option>
                    <option value="10DLC & Brand Registration">10DLC & Brand Registration</option>
                    <option value="Feature Request / Feedback">Feature Request / Feedback</option>
                    <option value="Other">Other</option>
                  </Form.Control>
                </Form.Group>

                <Form.Group className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="fw-bold text-dark mb-0" style={{ fontWeight: "600" }}>
                      Message <span className="text-danger">*</span>
                    </Form.Label>
                    <small className="text-muted fw-bold">
                      {formData.message.length}/1000
                    </small>
                  </div>
                  <Form.Control
                    as="textarea"
                    name="message"
                    rows={5}
                    maxLength={1000}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Describe how we can assist you..."
                    required
                    style={{ borderRadius: "8px", border: "1.5px solid #cbd5e1", padding: "12px" }}
                  />
                </Form.Group>

                <div className="d-flex justify-content-end">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="fw-bold text-white px-4 py-2"
                    style={{
                      backgroundColor: "#014c95",
                      borderColor: "#014c95",
                      borderRadius: "8px",
                      fontWeight: "700",
                      fontSize: "1rem",
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" /> Sending...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane me-2"></i> Submit Message
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Support Sidebar Info Card */}
        <Col lg={4} md={4} className="mb-4">
          <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "16px", border: "1px solid #e2e8f0", backgroundColor: "#f8fafc" }}>
            <Card.Body className="p-4">
              <h5 className="fw-bold mb-3" style={{ fontWeight: "700", color: "#014c95" }}>
                <i className="fas fa-info-circle me-2"></i> Support Details
              </h5>
              <div className="mb-3">
                <p className="mb-1 text-muted font-weight-bold" style={{ fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Response Time</p>
                <p className="fw-bold mb-0" style={{ color: "#1e293b" }}>
                  <i className="far fa-clock me-2 text-success"></i> Usually within 24 hours
                </p>
              </div>

              <hr style={{ borderColor: "#cbd5e1" }} />

              <div>
                <p className="mb-1 text-muted font-weight-bold" style={{ fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Business Hours</p>
                <p className="fw-bold mb-0" style={{ color: "#1e293b" }}>
                  <i className="far fa-calendar-alt me-2 text-info"></i> Mon – Fri, 9:00 AM – 6:00 PM EST
                </p>
              </div>
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-0" style={{ borderRadius: "16px", border: "1px solid #e2e8f0", backgroundColor: "#eff6ff" }}>
            <Card.Body className="p-4">
              <h6 className="fw-bold text-primary mb-2" style={{ fontWeight: "700" }}>
                <i className="fas fa-lightbulb me-2"></i> Fast Support Tip
              </h6>
              <p className="text-secondary small mb-0" style={{ fontSize: "0.9rem", lineHeight: "1.5" }}>
                If you are inquiring about a specific SMS campaign or phone number issue, please include the relevant Campaign Name or Phone Number in your message.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default ContactUs;
